// src/pages/refer/ReferOverviewPage.js
import React, { useEffect, useState } from "react";
import { getReferOverview } from "../../api/referApi";
import { useAuth } from "../../hooks/useAuth";
import { STATUS_LEVEL } from "../../utils/status";
import { getProfile } from "../../api/userApi";
import { FiUsers, FiAward, FiDollarSign, FiCopy, FiCheckCircle, FiTrendingUp } from "react-icons/fi";
import { FaMedal, FaStar, FaCrown } from "react-icons/fa";

function ReferOverviewPage() {
  const { user } = useAuth();
  const userId = user.user_id
  const referId = user.refer_id;
  
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    async function fetchRefer() {
      try {
        setLoading(true);
        setError("");

        const data = await getReferOverview(userId, referId);

        setStats({
          total_referred: Object.keys(data.referrals).length || 0,
          activated_count: Object.keys(data.referrals).length - Object.keys(data.newReferrals).length || 0,
          total_refer_income: data.referIncome || 0,
        });
        setReferredUsers(data.referrals || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load refer information.");
      } finally {
        setLoading(false);
      }
    }

    async function loadProfile() {
          try {
            setLoading(true);
            const data = await getProfile(userId);
            setProfile(data);
            //setProfile(authUser)
          } catch (err) {
            console.error(err);
            setError("Failed to load profile.");
          } finally {
            setLoading(false);
          }
        }
    loadProfile()
    fetchRefer();
  }, [userId, referId]);

  const formatAmount = (value) => {
    if (value == null) return "€ 0.00";
    return `€ ${Number(value).toFixed(2)}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleString();
  };

  const activatedCount = stats?.activated_count || 0;

  const bronzeTarget = 1;
  const silverTarget = 20;
  const goldTarget = 50;

  const bronzeReached = activatedCount >= bronzeTarget;
  const silverReached = activatedCount >= silverTarget;
  const goldReached = activatedCount >= goldTarget;

  const bronzeRemaining = Math.max(0, bronzeTarget - activatedCount);
  const silverRemaining = Math.max(0, silverTarget - activatedCount);
  const goldRemaining = Math.max(0, goldTarget - activatedCount);

  const handleCopyReferID = async () => {
    try {
      await navigator.clipboard.writeText(user?.refer_id || "");
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
            <FiUsers className="h-8 w-8 text-primary-600 animate-pulse" />
          </div>
          <p className="text-gray-600 font-medium">Loading refer information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-gradient-to-br from-red-50 to-rose-50 border-red-200 animate-fadeIn">
        <div className="flex items-start gap-3">
          <svg className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl shadow-glow">
            <FiUsers className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Refer & Earn</h1>
            <p className="text-gray-600 mt-1">
              Share your refer ID and unlock exclusive account levels
            </p>
          </div>
        </div>
      </div>

      {/* Refer ID Card - Enhanced */}
      <div className="card-gradient">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-4 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl shadow-md">
            <FiCopy className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Your Referral ID</h2>
            <p className="text-sm text-gray-600 mb-3">
              Share this ID with new users. When they activate, you earn rewards and progress towards higher levels!
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <input
                    type="text"
                    value={user?.refer_id || "-"}
                    readOnly
                    className="w-full px-4 py-3 bg-gradient-to-r from-primary-50 to-secondary-50 border-2 border-primary-200 rounded-lg font-mono text-lg font-bold text-primary-700 pr-12"
                  />
                  <button
                    onClick={handleCopyReferID}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    {copySuccess ? (
                      <FiCheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <FiCopy className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                </div>
              </div>
              {profile && (
                <div className="px-4 py-3 bg-white rounded-lg border-2 border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Current Status</div>
                  <div className="font-bold text-gray-900">{profile.status_level}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid - Enhanced */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-2">
                <FiUsers className="h-5 w-5 text-blue-600" />
                <div className="text-sm font-medium text-blue-700">Total Referred</div>
              </div>
              <div className="text-3xl font-bold text-blue-900">
                {stats.total_referred}
              </div>
              <div className="text-xs text-blue-600 mt-1">Users signed up</div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <FiCheckCircle className="h-5 w-5 text-green-600" />
                <div className="text-sm font-medium text-green-700">Activated Users</div>
              </div>
              <div className="text-3xl font-bold text-green-900">
                {stats.activated_count}
              </div>
              <div className="text-xs text-green-600 mt-1">Accounts activated</div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 border-2 border-amber-200">
              <div className="flex items-center gap-3 mb-2">
                <FiDollarSign className="h-5 w-5 text-amber-600" />
                <div className="text-sm font-medium text-amber-700">Total Earnings</div>
              </div>
              <div className="text-3xl font-bold text-amber-900">
                {formatAmount(stats.total_refer_income)}
              </div>
              <div className="text-xs text-amber-600 mt-1">Referral income</div>
            </div>
          </div>
        )}
      </div>

      {/* Level Progress - Advanced Design */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FiTrendingUp className="h-6 w-6 text-primary-600" />
          Account Level Progress
        </h2>
        <p className="text-gray-600 mb-6">
          Unlock higher levels by referring more users. Each level comes with exclusive benefits and reduced fees.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Bronze Account */}
          <div className={`relative overflow-hidden rounded-2xl border-3 border-orange-400 transition-all duration-300 ${
            bronzeReached ? "shadow-glow scale-105" : "shadow-card hover:shadow-soft opacity-75"
          }`}>
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 p-6 text-white relative">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

              <div className="relative flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                    <FaMedal className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Bronze Account</h3>
                    <p className="text-xs text-white/80">Level 1</p>
                  </div>
                </div>
                {bronzeReached && (
                  <div className="px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold border-2 border-white/40 animate-pulse">
                    UNLOCKED
                  </div>
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6">
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-orange-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-orange-700">Unlocks transfer & withdraw</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-orange-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-orange-700">Standard transaction fees</span>
                </div>
              </div>

              <div className="pt-4 border-t-2 border-orange-200">
                <div className="text-xs text-orange-600 mb-2">Requirement</div>
                <div className="text-sm font-bold text-orange-900 mb-3">
                  {bronzeReached ? "✓ Achieved" : `${bronzeRemaining} more activation${bronzeRemaining !== 1 ? "s" : ""} needed`}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-orange-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-600 to-amber-600 transition-all duration-500"
                      style={{ width: `${Math.min(100, (activatedCount / bronzeTarget) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-bold text-orange-700">{activatedCount}/{bronzeTarget}</span>
                </div>
              </div>
            </div>

            {bronzeReached && (
              <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                <div className="absolute top-4 right-[-32px] rotate-45 bg-white/30 backdrop-blur-md text-white text-xs font-bold py-1 px-10 text-center">
                  ACHIEVED
                </div>
              </div>
            )}
          </div>

          {/* Silver Account */}
          <div className={`relative overflow-hidden rounded-2xl border-3 border-gray-400 transition-all duration-300 ${
            silverReached ? "shadow-glow scale-105" : "shadow-card hover:shadow-soft opacity-75"
          }`}>
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600 p-6 text-white relative">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

              <div className="relative flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                    <FaStar className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Silver Account</h3>
                    <p className="text-xs text-white/80">Level 2</p>
                  </div>
                </div>
                {silverReached && (
                  <div className="px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold border-2 border-white/40 animate-pulse">
                    UNLOCKED
                  </div>
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-6">
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">All Bronze benefits</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">50% reduced fees</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Priority support</span>
                </div>
              </div>

              <div className="pt-4 border-t-2 border-gray-300">
                <div className="text-xs text-gray-600 mb-2">Requirement</div>
                <div className="text-sm font-bold text-gray-900 mb-3">
                  {silverReached ? "✓ Achieved" : `${silverRemaining} more activation${silverRemaining !== 1 ? "s" : ""} needed`}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-gray-400 to-gray-600 transition-all duration-500"
                      style={{ width: `${Math.min(100, (activatedCount / silverTarget) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-bold text-gray-700">{activatedCount}/{silverTarget}</span>
                </div>
              </div>
            </div>

            {silverReached && (
              <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                <div className="absolute top-4 right-[-32px] rotate-45 bg-white/30 backdrop-blur-md text-white text-xs font-bold py-1 px-10 text-center">
                  ACHIEVED
                </div>
              </div>
            )}
          </div>

          {/* Gold Account */}
          <div className={`relative overflow-hidden rounded-2xl border-3 border-yellow-500 transition-all duration-300 ${
            goldReached ? "shadow-glow scale-105" : "shadow-card hover:shadow-soft opacity-75"
          }`}>
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 p-6 text-white relative">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

              <div className="relative flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                    <FaCrown className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Gold Account</h3>
                    <p className="text-xs text-white/80">Level 3</p>
                  </div>
                </div>
                {goldReached && (
                  <div className="px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold border-2 border-white/40 animate-pulse">
                    UNLOCKED
                  </div>
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6">
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-yellow-700">All Silver benefits</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-yellow-700">Zero transaction fees</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-yellow-700">VIP support & perks</span>
                </div>
              </div>

              <div className="pt-4 border-t-2 border-yellow-200">
                <div className="text-xs text-yellow-600 mb-2">Requirement</div>
                <div className="text-sm font-bold text-yellow-900 mb-3">
                  {goldReached ? "✓ Achieved" : `${goldRemaining} more activation${goldRemaining !== 1 ? "s" : ""} needed`}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-yellow-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-500"
                      style={{ width: `${Math.min(100, (activatedCount / goldTarget) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-bold text-yellow-700">{activatedCount}/{goldTarget}</span>
                </div>
              </div>
            </div>

            {goldReached && (
              <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                <div className="absolute top-4 right-[-32px] rotate-45 bg-white/30 backdrop-blur-md text-white text-xs font-bold py-1 px-10 text-center">
                  ACHIEVED
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Referred users table - Enhanced */}
      <div className="card-gradient">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl shadow-md">
            <FiUsers className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Referred Users</h2>
            <p className="text-sm text-gray-600">Track all users who signed up with your referral ID</p>
          </div>
        </div>

        {referredUsers.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <FiUsers className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium mb-1">No referrals yet</p>
            <p className="text-sm text-gray-500">
              Share your refer ID to start earning and upgrading your level
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-primary-50 to-secondary-50 border-y-2 border-primary-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">User Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Joined At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referredUsers.map((u, idx) => (
                  <tr key={u.user_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-900">{u.user_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{u.name || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-600">{u.email || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-600">{u.phone || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border-2 " +
                          (u.status === 5
                            ? "bg-yellow-50 text-yellow-800 border-yellow-300"
                            : u.status === 4
                            ? "bg-gray-50 text-gray-800 border-gray-300"
                            : u.status === 3
                            ? "bg-orange-50 text-orange-800 border-orange-300"
                            : u.status === 2
                            ? "bg-green-50 text-green-800 border-green-300"
                            : "bg-red-50 text-red-700 border-red-200")
                        }
                      >
                        {u.status_level || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{formatDateTime(u.created_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferOverviewPage;
